import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import { updateProfile, updateCalendarPreference } from '../../services/userService';
import useAuth from '../../hooks/useAuth';
import { useCalendar } from '../../context/CalendarContext';
import { MdPerson, MdPhone, MdSave, MdCalendarMonth, MdPhotoCamera } from 'react-icons/md';
import AvatarUploader from '../../components/AvatarUploader';

function StudentSettings() {
  document.title = 'Settings — TrainAxis';
  const { user, updateUser } = useAuth();
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: { name: user?.name || '', phone: user?.phone || '' },
  });

  const profileMutation = useMutation({
    mutationFn: updateProfile,
    onSuccess: ({ data }) => {
      toast.success('Profile updated');
      if (data.user) updateUser({ name: data.user.name, phone: data.user.phone });
    },
    onError: () => toast.error('Failed to update profile'),
  });

  return (
    <div className="space-y-5 max-w-lg">
      <Card>
        <div className="flex items-center gap-2 mb-4">
          <MdPhotoCamera className="text-primary text-xl" />
          <h2 className="font-semibold text-secondary text-sm">Profile Photo</h2>
        </div>
        <div className="flex items-center gap-4">
          <AvatarUploader
            currentPhotoUrl={user?.profilePhoto || null}
            studentName={user?.name}
            uploadUrl="/api/students/me/photo"
            size={96}
            onUploaded={(url) => { updateUser({ profilePhoto: url }); toast.success('Profile photo updated'); }}
          />
          <p className="text-xs text-gray-400 max-w-xs leading-relaxed">Click your photo to upload or change it.</p>
        </div>
      </Card>

      <Card>
        <div className="flex items-center gap-2 mb-4">
          <MdPerson className="text-primary text-xl" />
          <h2 className="font-semibold text-secondary text-sm">My Profile</h2>
        </div>
        <form onSubmit={handleSubmit(d => profileMutation.mutate(d))} className="space-y-3">
          <Input label="Display Name" icon={MdPerson} error={errors.name?.message}
            {...register('name', { required: 'Name is required', minLength: { value: 2, message: 'At least 2 characters' } })} />
          <Input label="Phone" icon={MdPhone} type="tel" maxLength={10} error={errors.phone?.message}
            {...register('phone', { pattern: { value: /^(97|98)\d{8}$/, message: 'Enter a valid Nepal mobile number' } })} />
          <Button type="submit" loading={profileMutation.isPending}>
            <MdSave /> Save Profile
          </Button>
        </form>
      </Card>

      {/* Theme buttons removed — the sun/moon toggle in the top navbar now owns this, writing
          through the same applyTheme/updateTheme path used everywhere else. */}
      <p className="text-xs text-subtle">
        Dashboard theme is switched from the sun/moon icon in the top bar.
      </p>

      {/* Calendar Preference card removed (Step 2): dates now render Bikram Sambat primary with
          a muted AD secondary everywhere — no per-user choice. */}
    </div>
  );
}

export default StudentSettings;
